import { Action, Module, Mutation, VuexModule } from 'vuex-module-decorators';
import { ScenarioExt } from '@/classes/scenario/ScenarioExt';
import { ScenarioGeneratorExt } from '@/classes/scenario/genset/ScenarioGeneratorExt';
import { FieldStatus } from '@/classes/FieldStatus';
import { GensetTypeStats } from '@/interfaces/BackendGensetStats';
import { ScenarioPayloads } from '@/store/modules/scenario';

interface Dependencies {
  get: <T>(url: string) => Promise<{data: T}>
}

export interface PreSizingResult {
  resultsFilename: string
  PLoad?: number[]
  PGenset?: number[]
  PBlackout?: number[]
  Blackout_flag?: (0 | 1)[]
  PGrid?: number[],
  stats?: GensetTypeStats[]
}

export interface SelectedDay {
  day: number
  month: number
}

export function buildPresizingModule({ get }: Dependencies) {
  @Module({
    namespaced: true
  })
  class PreSizingModule extends VuexModule {
    private presizingData: Record<string, PreSizingResult> = {};
    private selectedDay: Record<string, SelectedDay> = {};

    @Action
    private async generate(): Promise<PreSizingResult> {
      this.context.commit('scenario/setAlgoIsRunning', true, { root: true });
      try {
        await this.context.dispatch('scenario/updateScenario', this.context.rootGetters['scenario/getFields'], { root: true });
        const { data } = await get<PreSizingResult>(`/algo/executePreSizing/${this.currentScenarioId}`);
        this.context.commit('setData', [this.currentScenarioId, data]);
        const scenario: ScenarioExt = this.context.rootGetters['scenario/getFields'];
        const numberOfHourOfBlackout = data.Blackout_flag?.reduce((total: number, blackoutHour: 0 | 1) => total + blackoutHour, 0) ?? 0;
        const newScenario = new ScenarioExt({
          ...scenario,
          generator: new ScenarioGeneratorExt({
            ...scenario.generator,
            preSizing: { done: true, blackout: numberOfHourOfBlackout }
          })
        });
        this.context.dispatch(...ScenarioPayloads.SaveFields.buildPayload({ scenario: newScenario }));
        return data;
      } catch (error) {
        this.context.commit('invalidateCurrent', this.currentScenarioId);
        throw error;
      } finally {
        this.context.commit('scenario/setAlgoIsRunning', false, { root: true });
      }
    }

    @Action
    private async invalidate(): Promise<void> {
      this.context.commit('invalidateCurrent', this.currentScenarioId);
      const scenario: ScenarioExt = this.context.rootGetters['scenario/getFields'];
      const newScenario = new ScenarioExt({
        ...scenario,
        generator: new ScenarioGeneratorExt({
          ...scenario.generator,
          preSizing: { done: false, blackout: 0 }
        })
      });
      this.context.dispatch(...ScenarioPayloads.SaveFields.buildPayload({ scenario: newScenario }));
    }

    @Action
    private async updateEnergyChartSelectedDay(selectedDay: Partial<SelectedDay>): Promise<void> {
      const selectedDayWithDefault: SelectedDay = {
        ...this.context.getters.energyChartSelectedDay,
        ...selectedDay
      };
      this.context.commit('setEnergyChartSelectedDay', [this.currentScenarioId, selectedDayWithDefault]);
    }

    @Mutation
    private async invalidateCurrent(currentId: string): Promise<void> {
      const filteredCurrentScenario = Object.entries(this.presizingData)
        .filter(([id]) => id !== currentId);
      this.presizingData = Object.fromEntries(filteredCurrentScenario);
    }

    @Mutation
    private setData([id, result]: [string, PreSizingResult]): void {
      this.presizingData = {
        ...this.presizingData,
        [id]: result
      };
    }

    @Mutation
    private setEnergyChartSelectedDay([id, newSelectedDay]: [string, SelectedDay]): void {
      this.selectedDay = {
        ...this.selectedDay,
        [id]: newSelectedDay
      };
    }

    get currentScenarioId(): string {
      const scenario = this.context.rootGetters['scenario/getFields'];
      return scenario.uuid;
    }

    get data(): PreSizingResult | null {
      return this.presizingData[this.currentScenarioId] ?? null;
    }

    get energyChartSelectedDay(): SelectedDay {
      const januaryTheFirst = {
        day: 0,
        month: 0
      };
      return this.selectedDay[this.currentScenarioId] ?? januaryTheFirst;
    }

    get errorFields(): FieldStatus[] {
      const fieldErrors: FieldStatus[] = this.context.rootGetters['scenario/getFieldErrors'];
      return fieldErrors
        .filter(status => status.isUsedForPreSizing());
    }
  }

  return PreSizingModule;
}
